import { createSelector } from 'reselect';

import { dateUtils, msToAmPmHours } from '~/routes/utils';
import { bookingsSelectors } from '~/api';
import { envConfigSelectors } from '~/components';

export const selectIcsEventStartDate = createSelector(
  bookingsSelectors.selectDateSlot,
  bookingsSelectors.selectTimeSlotFrom,
  (date, from) => dateUtils.getIcsDateTime(dateUtils.getDateTimeObj(date, from)),
);

export const selectIcsEventEndDate = createSelector(
  bookingsSelectors.selectDateSlot,
  bookingsSelectors.selectTimeSlotTo,
  (date, to) => dateUtils.getIcsDateTime(dateUtils.getDateTimeObj(date, to)),
);

export const selectIcsEventGeolocation = createSelector(
  bookingsSelectors.selectStoreLatitude,
  bookingsSelectors.selectStoreLongitude,
  (latitude, longitude) => (latitude && longitude ? `${latitude};${longitude}` : ''),
);

export const selectIcsEventFormattedTime = createSelector(
  bookingsSelectors.selectTimeSlotFrom,
  bookingsSelectors.selectTimeSlotTo,
  (from, to) => `${msToAmPmHours(from)} - ${msToAmPmHours(to)}`,
);

export const selectAppointmentTypeName = (appointmentTypeId) =>
  createSelector(
    envConfigSelectors.selectIsServiceNameIncludedToAppointmentTypeName(appointmentTypeId),
    bookingsSelectors.selectDepartmentName,
    bookingsSelectors.selectAppointmentName,
    (isServiceNameIncludedToAppointmentTypeName, serviceName, appointmentTypeName) =>
      `${
        isServiceNameIncludedToAppointmentTypeName
          ? `${serviceName}: ${appointmentTypeName}`
          : appointmentTypeName
      }`,
  );

export const selectIcsEventTitle = (appointmentTypeId) =>
  createSelector(
    envConfigSelectors.selectIcsEvent(appointmentTypeId),
    selectAppointmentTypeName(appointmentTypeId),
    envConfigSelectors.selectIsPartnerSelectionEnabled(appointmentTypeId),
    bookingsSelectors.selectPartnerName,
    bookingsSelectors.selectDepartmentName,
    (
      icsEventConfig = {},
      appointmentTypeTitle,
      isPartnerSelectionEnabled,
      partnerName,
      departmentName,
    ) => {
      const stylistTitleSection = `${isPartnerSelectionEnabled ? ` with ${partnerName}` : ''}`;

      return (
        icsEventConfig.getTitle?.({ departmentName, appointmentTypeTitle }) ||
        `John Lewis ${appointmentTypeTitle} appointment${stylistTitleSection}`
      );
    },
  );

export const selectAppointmentInfoSection = (appointmentTypeId) =>
  createSelector(
    envConfigSelectors.selectIcsEvent(appointmentTypeId),
    selectAppointmentTypeName(appointmentTypeId),
    (icsEventConfig = {}, appointmentTypeName) =>
      icsEventConfig.getAppointmentInfoSection?.(appointmentTypeName) ||
      `John Lewis & Partners ${appointmentTypeName}`,
  );

export const selectIcsEventTimeBeforeAppointment = (appointmentTypeId) =>
  createSelector(
    envConfigSelectors.selectIcsEvent(appointmentTypeId),
    (event) => event?.timeBeforeAppointment || 5,
  );

export const selectPartnerInfoSection = (appointmentTypeId) =>
  createSelector(
    envConfigSelectors.selectIsPartnerSelectionEnabled(appointmentTypeId),
    envConfigSelectors.selectPartnerTitle,
    bookingsSelectors.selectPartnerName,
    (isPartnerSelectionEnabled, partnerPosition, partnerName) =>
      isPartnerSelectionEnabled ? `${partnerPosition}:\\n${partnerName}\\n\\n` : '',
  );

export const selectCustomAdditionalInfoSection = (appointmentTypeId) =>
  createSelector(
    envConfigSelectors.selectIcsEvent(appointmentTypeId),
    bookingsSelectors.selectProducts,
    (event = {}, products) => event.getCustomAdditionalInfoSection?.({ products }),
  );

export const selectFormattedStoreAddress = createSelector(
  bookingsSelectors.selectStoreName,
  bookingsSelectors.selectStoreAddressLines,
  (shopName, storeAddressLines) =>
    `${shopName},\\n${storeAddressLines?.split(', ')?.join(',\\n') ?? ''}`,
);

export const selectFormattedCustomerAddress = (appointmentTypeId) =>
  createSelector(
    envConfigSelectors.selectCustomerAddressFromQuestion(appointmentTypeId),
    (customerAddress) => customerAddress?.split(', ')?.join(',\\n'),
  );

export const selectAtHomeAppointmentAdditionalInfoSection = (appointmentTypeId) =>
  createSelector(
    selectFormattedCustomerAddress(appointmentTypeId),
    envConfigSelectors.selectIsVirtualAppointment(appointmentTypeId),
    (formattedCustomerAddress, isVirtualAppointment) =>
      formattedCustomerAddress && !isVirtualAppointment
        ? `Location:\\n ${formattedCustomerAddress}`
        : '',
  );

export const selectIcsEventLocation = (appointmentTypeId) =>
  createSelector(
    envConfigSelectors.isInStoreAppointmentType(appointmentTypeId),
    bookingsSelectors.selectFormattedAddress,
    envConfigSelectors.selectCustomerAddressFromQuestion(appointmentTypeId),
    (isInStoreAppointmentType, selectFormattedAddress, customerAddress) =>
      isInStoreAppointmentType ? selectFormattedAddress : (customerAddress ?? ''),
  );

export const selectInStoreAppointmentAdditionalInfoSection = (appointmentTypeId) =>
  createSelector(
    envConfigSelectors.isInStoreAppointmentType(appointmentTypeId),
    selectIcsEventTimeBeforeAppointment(appointmentTypeId),
    selectFormattedStoreAddress,
    (isInStoreAppointment, time, formattedStoreAddress) =>
      isInStoreAppointment
        ? `Please aim to arrive ${time} minutes before your appointment time.` +
          '\\n\\nShop and location:' +
          `\\n${formattedStoreAddress}`
        : '',
  );
export const selectIcsEventDescription = (appointmentTypeId) =>
  createSelector(
    selectAppointmentTypeName(appointmentTypeId),
    selectAppointmentInfoSection(appointmentTypeId),
    selectPartnerInfoSection(appointmentTypeId),
    bookingsSelectors.selectDateSlot,
    selectIcsEventFormattedTime,
    selectCustomAdditionalInfoSection(appointmentTypeId),
    selectAtHomeAppointmentAdditionalInfoSection(appointmentTypeId),
    selectInStoreAppointmentAdditionalInfoSection(appointmentTypeId),
    (
      appointmentTypeName,
      appointmentInfoSection,
      partnerInfoSection,
      dateString,
      formattedTime,
      customAdditionalInfoSection,
      atHomeAppointmentAdditionalInfoSection,
      inStoreAppointmentAdditionalInfoSection,
    ) => {
      const date = dateUtils.getDateFromString(dateString);

      return (
        'You can find your appointment details below.\\n\\n' +
        'Appointment type:\\n' +
        `${appointmentInfoSection}\\n\\n` +
        `${partnerInfoSection}` +
        'Date and time:\\n' +
        `${dateUtils.getDayFullName(date.getDay())}, ` +
        `${date.getDate()} ${dateUtils.getMonthFullName(
          date.getMonth(),
        )} ${date.getFullYear()}\\n` +
        `${formattedTime}` +
        `${
          customAdditionalInfoSection ||
          `\\n\\n${
            atHomeAppointmentAdditionalInfoSection || inStoreAppointmentAdditionalInfoSection
          }`
        }`
      );
    },
  );

export const icsSelectors = {
  selectIcsEventLocation,
  selectIcsEventTitle,
  selectIcsEventStartDate,
  selectIcsEventEndDate,
  selectIcsEventGeolocation,
  selectIcsEventDescription,
};
